/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { Region } from '@sg/garnish';
import { findCenterRegion } from '@sg/garnish';

import { LocationsMap } from '@order/components';
import { useCustomer } from '@order/Customer';

import type { LocationSearchMachineContext } from '../../machine';
import type { LocationNavigationEntryPoint } from '../../machine';
import {
  generateMapPinsForLocations,
  getActiveLocationsCoordinates,
} from './LocationSearchMap.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchMap = memo((props: LocationSearchMapProps) => {
  const {
    locationSearchType,
    locations,
    deliveryLocation,
    currentPosition,
    focusedPinId,
    isLoadingLocations,
    onSearchAreaPress,
    onGetCurrentPosition,
    onLocationsSearchAreaChange,
    onMapPinPress,
    onLocationPinPress,
    onDeliveryPinPress,
  } = props;

  const { customer } = useCustomer();

  const customerName = customer?.firstName ?? '';
  const isDeliveryLocationSearchType = locationSearchType === 'delivery';

  // ─── State ───────────────────────────────────────────────────────────

  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const updateMapDimensions = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;

    setMapDimensions({ width, height });
  }, []);

  const clearFocusedPinId = useCallback(() => {
    onMapPinPress(undefined);
  }, [onMapPinPress]);

  const handleOnCalloutPress = useCallback<OnPinCalloutPress>(
    ({ restaurantSlug }) => {
      if (!restaurantSlug) return;

      onLocationPinPress({ restaurantSlug }, { entryPoint: 'pin' });
    },
    [onLocationPinPress],
  );

  const handleOnDeliveryCalloutPress = useCallback<OnPinCalloutPress>(
    ({ restaurantSlug }) => {
      if (!restaurantSlug) return;

      onDeliveryPinPress?.({ restaurantSlug }, { entryPoint: 'pin' });
    },
    [onDeliveryPinPress],
  );

  const onCalloutPress = isDeliveryLocationSearchType
    ? handleOnDeliveryCalloutPress
    : handleOnCalloutPress;

  const handleOnSearchAreaPress = isDeliveryLocationSearchType
    ? undefined
    : onSearchAreaPress;

  // ─── Derived Data ────────────────────────────────────────────────────

  const activeLocationsCoordinates = useMemo(() => {
    const activeLocations = getActiveLocationsCoordinates({
      locationSearchType,
      locations,
      deliveryLocation,
    });

    // include customer's current position (if available)
    if (currentPosition) {
      const customerCurrentPosition = {
        lat: currentPosition.latitude,
        lng: currentPosition.longitude,
      };

      return [...activeLocations, customerCurrentPosition];
    }

    return activeLocations;
  }, [currentPosition, deliveryLocation, locationSearchType, locations]);

  const pins = useMemo(() => {
    return generateMapPinsForLocations({
      currentPosition,
      locationSearchType,
      customerName,
      locations,
      deliveryLocation,
      focusedPinId,
    });
  }, [
    currentPosition,
    customerName,
    deliveryLocation,
    focusedPinId,
    locationSearchType,
    locations,
  ]);

  const region = useMemo(() => {
    if (activeLocationsCoordinates.length === 0) return;

    const pinsRegion = findCenterRegion(
      activeLocationsCoordinates,
      mapDimensions,
    );

    const isValidRegion =
      !Number.isNaN(pinsRegion.lat) && !Number.isNaN(pinsRegion.lng);

    return isValidRegion ? pinsRegion : DOWNTOWN_LA_REGION;
  }, [activeLocationsCoordinates, mapDimensions]);

  // ─────────────────────────────────────────────────────────────────────
  return (
    <View style={styles.container} onLayout={updateMapDimensions}>
      <LocationsMap
        pins={pins}
        region={region ?? DOWNTOWN_LA_REGION}
        isLoadingMapLocations={isLoadingLocations}
        isSearchAreaBtnEnabled={locationSearchType !== 'delivery'}
        onRegionChange={onLocationsSearchAreaChange}
        onSearchAreaPress={handleOnSearchAreaPress}
        onGeolocateUserBtnPress={onGetCurrentPosition}
        onMapPress={clearFocusedPinId}
        onCalloutPress={onCalloutPress}
        onPinPress={onMapPinPress}
      />
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DOWNTOWN_LA_REGION = { lat: 34.0522, lng: -118.2437 };

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchMapProps = {
  readonly locationSearchType: LocationSearchMachineContext['locationSearchType'];
  readonly locations: LocationSearchMachineContext['locations'];
  readonly deliveryLocation: LocationSearchMachineContext['deliveryLocation'];
  readonly currentPosition: LocationSearchMachineContext['currentPosition'];
  readonly focusedPinId: string | undefined;
  readonly isLoadingLocations: boolean;
  readonly onLocationsSearchAreaChange: (region: Region) => void;
  readonly onSearchAreaPress: () => void;
  readonly onGetCurrentPosition: () => void;
  readonly onMapPinPress: (id: string | undefined) => void;
  readonly onLocationPinPress: (
    location: { readonly restaurantSlug: string },
    options?: {
      readonly entryPoint?: LocationNavigationEntryPoint;
    },
  ) => void;
  readonly onDeliveryPinPress: (
    location: { readonly restaurantSlug: string },
    options?: {
      readonly entryPoint?: LocationNavigationEntryPoint;
    },
  ) => void;
};

type OnPinCalloutPress = NonNullable<
  ComponentProps<typeof LocationsMap>['onCalloutPress']
>;
